/**
 * Judge.me Custom Reviews Carousel
 * Extracts reviews from Judge.me widgets and displays in custom carousel
 * Uses DOM extraction only (no API calls due to CORS)
 */

class JudgeMeCarousel {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    this.reviews = [];
    this.swiper = null;
    
    if (!this.container) {
      return;
    }
    
    this.init();
  }

  async init() {
    // Wait for Judge.me widgets to load
    await this.waitForJudgeMe();
    
    // Extract reviews from DOM
    this.extractFromDOM();
    
    if (this.reviews.length > 0) {
      this.renderCarousel();
    } else {
      this.showNoReviews();
    }
  }

  async waitForJudgeMe() {
    // Wait for Judge.me widgets to be in the DOM
    let attempts = 0;
    while (attempts < 100) { // Wait up to 10 seconds
      const hasWidgets = document.querySelectorAll('.jdgm-widget').length > 0;
      
      if (hasWidgets) {
        // Wait extra time for reviews to populate
        await this.delay(1500);
        return true;
      }
      await this.delay(100);
      attempts++;
    }
    
    return false;
  }

  extractFromDOM() {
    // Find testimonials carousel widget
    const testimonialsWidget = document.querySelector('.jdgm-testimonials-carousel');
    // Keep a reference so we can hide it once our custom carousel is rendered
    this.testimonialsWidget = testimonialsWidget || this.testimonialsWidget;
    
    let testimonialCards = [];
    
    if (testimonialsWidget) {
      // Find all testimonial cards ONLY within the specific testimonials widget
      testimonialCards = testimonialsWidget.querySelectorAll('.jdgm-testimonial, .jdgm-card.jdgm-testimonial');
    } else {
      // Fallback: look for reviews in other Judge.me widgets
      const allJudgeMeWidgets = document.querySelectorAll('.jdgm-widget');
      
      allJudgeMeWidgets.forEach(widget => {
        const cards = widget.querySelectorAll('.jdgm-review, .jdgm-testimonial, .review-item');
        testimonialCards = [...testimonialCards, ...cards];
      });
    }

    const seenIds = new Set(); // Track unique review IDs
    
    // Parse each testimonial card
    testimonialCards.forEach((element, index) => {
      const review = this.parseReviewElement(element, index);
      if (review && !seenIds.has(review.id)) {
        seenIds.add(review.id);
        this.reviews.push(review);
      }
    });
  }

  parseReviewElement(element, index) {
    try {
      // Extract data from Judge.me testimonial/review structure
      const textEl = element.querySelector('.jdgm-text, .jdgm-card-content p, .jdgm-rev__text, .jdgm-rev__prod-cont p, .jdgm-rev__body');
      const ratingEl = element.querySelector('.jdgm-rating-section, .jdgm-stars, .jdgm-histogram');
      const authorEl = element.querySelector('.jdgm-reviewer-name, .jdgm-reviewer-info, .jdgm-rev__author');
      
      const body = textEl?.textContent?.trim() || '';
      const rating = this.extractRating(ratingEl || element);
      const author = authorEl?.textContent?.trim() || 'Anonymous';
      
      // Only include if we have text or rating
      if (body || rating) {
        return {
          id: element.dataset?.reviewId || element.dataset?.testimonial || `review-${index}`,
          rating: rating || 5,
          title: '', // Testimonials don't have titles
          body: body,
          author: author,
          date: '' // Not visible in testimonials
        };
      }
    } catch (e) {
      // Silently handle parse errors
    }
    return null;
  }

  extractRating(element) {
    if (!element) return null;
    
    // Try data attributes first
    if (element.dataset?.rating) {
      return parseFloat(element.dataset.rating);
    }
    
    // Try aria-label
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) {
      const match = ariaLabel.match(/(\d+\.?\d*)/);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    
    // Count Judge.me filled stars (.jdgm--on class)
    const stars = element.querySelectorAll('.jdgm-star.jdgm--on, .fa-star, .jdgm-star-filled, .star.filled, .star.full');
    if (stars.length > 0) {
      return stars.length;
    }
    
    return null;
  }

  renderCarousel() {
    // Calculate average rating
    const avgRating = this.reviews.length > 0 
      ? (this.reviews.reduce((sum, r) => sum + r.rating, 0) / this.reviews.length).toFixed(1)
      : '0.0';
    
    // Populate header rating if it exists
    const sectionId = this.container.id.replace('judge-me-carousel-container-', '');
    const headerRatingEl = document.getElementById(`jdgm-header-rating-${sectionId}`);
    if (headerRatingEl) {
      headerRatingEl.innerHTML = `
        <div class="jdgm-stars">
          ${this.generateStarDisplay(parseFloat(avgRating))}
        </div>
        <span class="jdgm-rating-text">${avgRating} Ratings (${this.reviews.length})</span>
      `;
    } else {
      // Fallback: look for any jdgm-header-rating element
      const fallbackEl = document.querySelector('.jdgm-header-rating');
      if (fallbackEl) {
        fallbackEl.innerHTML = `
          <div class="jdgm-stars">
            ${this.generateStarDisplay(parseFloat(avgRating))}
          </div>
          <span class="jdgm-rating-text">${avgRating} Ratings (${this.reviews.length})</span>
        `;
      }
    }
    
    // Generate unique IDs based on container ID
    const swiperId = `judge-me-swiper-${this.container.id}`;
    const nextBtnClass = `judge-me-next-${this.container.id}`;
    const prevBtnClass = `judge-me-prev-${this.container.id}`;
    
    const reviewCardsHTML = this.reviews.map(review => this.createReviewCard(review));
    
    const carouselHTML = `
      <div class="judge-me-carousel-wrapper">
        <div class="swiper judge-me-swiper" id="${swiperId}">
          <div class="swiper-wrapper">
            ${reviewCardsHTML.join('')}
          </div>
          
          <div class="swiper-button-next ${nextBtnClass}"></div>
          <div class="swiper-button-prev ${prevBtnClass}"></div>
        </div>
      </div>
    `;
    
    this.container.innerHTML = carouselHTML;
    
    // Hide original Judge.me testimonials widget after successful render
    if (this.testimonialsWidget) {
      this.testimonialsWidget.style.display = 'none';
    }
    
    this.initSwiper(swiperId, nextBtnClass, prevBtnClass);
  }

  createReviewCard(review) {
    const stars = this.generateStars(review.rating);
    
    const cardHTML = `
      <div class="swiper-slide">
        <div class="judge-me-review-card">
          <div class="review-header">
            <div class="review-stars">${stars}</div>
             <div class="review-author-line">
            <span class="review-author">By ${this.escapeHtml(review.author)}</span>
          </div>
          </div>
         
          ${review.title ? `<h3 class="review-title">${this.escapeHtml(review.title)}</h3>` : ''}
          <p class="review-body">${this.escapeHtml(review.body || '')}</p>
        </div>
      </div>
    `;
    
    return cardHTML;
  }

  generateStars(rating) {
    // Return a single filled star icon with the rating number
    return '★ ' + rating.toFixed(1);
  }

  generateStarDisplay(rating) {
    // Return a single filled star for the header
    return '<span class="jdgm-star jdgm--on"></span>';
  }

  initSwiper(swiperId, nextBtnClass, prevBtnClass) {
    if (typeof Swiper === 'undefined') {
      return;
    }
    
    this.swiper = new Swiper(`#${swiperId}`, {
      slidesPerView: 1,
      spaceBetween: 20,
      loop: this.reviews.length > 1,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: `.${nextBtnClass}`,
        prevEl: `.${prevBtnClass}`,
      },
      breakpoints: {
        640: {
          slidesPerView: 1,
          spaceBetween: 20,
        },
        768: {
          slidesPerView: 1,
          spaceBetween: 25,
        },
        1024: {
          slidesPerView: 1,
          spaceBetween: 30,
        },
      },
    });
  }

  showNoReviews() {
    this.container.innerHTML = `
      <div class="judge-me-no-reviews">
        <p>No reviews available yet. Be the first to review!</p>
      </div>
    `;
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Auto-initialize if data attributes are present
document.addEventListener('DOMContentLoaded', function() {
  const carouselContainers = document.querySelectorAll('[data-judge-me-carousel]');
  
  carouselContainers.forEach((container) => {
    const containerId = container.id || `judge-me-carousel-${Math.random().toString(36).substr(2, 9)}`;
    container.id = containerId;
    
    new JudgeMeCarousel(containerId);
  });
});

// Export for manual initialization
window.JudgeMeCarousel = JudgeMeCarousel;
